import { useState } from 'react';

import { UploadedFile } from '@backrs_ui/common-ui';

import { DocumentFileUpload } from '@backrs_ui/common-ui/src/molecules/Profile/DocumentFileUpload';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { DOCUMENT_TYPES } from '@backrs_ui/grow-ui/src/models/models';
import { showSnackbar } from '@backrs_ui/grow-ui/src/redux/appState/appState';
import { useIngestLinkMutation } from '@backrs_ui/grow-ui/src/redux/coachLubav/coachLubav.api';
import { useAppDispatch, useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import {
  StoryDocument,
  useUpdateUserProfileMutation,
} from '@backrs_ui/grow-ui/src/redux/profile/profile';
import { uploadFile } from '@backrs_ui/grow-ui/src/utils/uploadFile';

import { Box, Theme, SxProps, Typography } from '@mui/material';

import { ProfileDocumentLinkInput } from './atoms/ProfileDocumentLinkInput';
import {
  DocumentUploadSource,
  ProfileDocumentUploadModal,
} from './atoms/ProfileDocumentUploadModal';
import { FileUploadConfig } from './types/profile.types';

const PROFILE_FORM_DOCUMENT_STRINGS = appStrings.modules.profile.modalUploader.document;
const LINK_STRINGS = appStrings.modules.profile.modalUploader.document.link;

type ProfileFormDocumentProps = Readonly<{
  onSuccess?: () => void;
  onError?: (error: Error) => void;
  fileUploadConfig: FileUploadConfig;
}>;

export const ProfileFormDocument = ({
  onSuccess,
  onError,
  fileUploadConfig,
}: ProfileFormDocumentProps) => {
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [updateUserProfile] = useUpdateUserProfileMutation();
  const [ingestLink] = useIngestLinkMutation();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.user);

  const [modalSource, setModalSource] = useState<DocumentUploadSource | null>(null);
  const [inProgressFileUpload, setInProgressFileUpload] = useState<File | null>(null);
  const [fileQueue, setFileQueue] = useState<{ file: File; mediaId: number }[]>([]);

  const [modalOpen, setModalOpen] = useState(false);

  const handleFileSelect = async (files: File | File[]) => {
    const fileArray = Array.isArray(files) ? files : [files];

    setError(null);
    try {
      setIsUploading(true);

      const uploadedFiles: { file: File; mediaId: number }[] = [];

      for (const file of fileArray) {
        setInProgressFileUpload(file);
        const result = await uploadFile({ file });

        uploadedFiles.push({ file, mediaId: result.mediaId });
      }

      if (fileUploadConfig.overrideModal) {
        if (!user?.id) {
          throw new Error('User ID is required');
        }
        // Make the API request directly when modal is overridden
        for (const { mediaId } of uploadedFiles) {
          const profileData: Omit<StoryDocument, 'id' | 'media_url' | 'profile_option_answers_id'> =
            {
              user_id: user.id,
              category_id: fileUploadConfig.categoryId,
              question_id: fileUploadConfig.questionId,
              media_type: 'video',
              visibility: fileUploadConfig.visibility,
              option_id: fileUploadConfig.optionId as
                | 'resume'
                | 'transcript'
                | 'other'
                | 'personal_assessment'
                | null,
              media_id: mediaId,
              title: '',
              description: '',
            };

          await updateUserProfile({
            field: fileUploadConfig.key,
            value: profileData,
          }).unwrap();
        }

        onSuccess?.();
      } else {
        setFileQueue(uploadedFiles);
        setModalSource({
          kind: 'file',
          file: uploadedFiles[0].file,
          mediaId: uploadedFiles[0].mediaId,
        });
        setModalOpen(true);
      }
    } catch (error) {
      onError?.(error as Error);
    } finally {
      setIsUploading(false);
      setInProgressFileUpload(null);
    }
  };

  const handleLinkSubmit = (url: string) => {
    // Open the modal immediately and ingest in the background — the ingest can be
    // slow, and the modal's form doesn't need the result until submit.
    setFileQueue([]);
    setModalSource({ kind: 'link', url, result: null });
    setModalOpen(true);

    ingestLink({ url, classify_as: Object.values(DOCUMENT_TYPES) })
      .unwrap()
      .then((result) => {
        setModalSource((prev) =>
          prev?.kind === 'link' && prev.url === url ? { ...prev, result } : prev,
        );
      })
      .catch((error) => {
        setModalOpen(false);
        setModalSource(null);
        dispatch(showSnackbar({ message: LINK_STRINGS.addError, severity: 'error' }));
        onError?.(error as Error);
      });
  };

  const getFileSize = (file: File | undefined) => {
    if (!file) {
      return '';
    }

    if (file.size > 1024 * 1024) {
      return `${(file.size / 1024 / 1024).toFixed(2)} MB`;
    }

    return `${((file?.size || 0) / 1024).toFixed(2)} KB`;
  };

  return (
    <Box>
      <Box sx={uploadGroupSx}>
        <DocumentFileUpload
          key={fileUploadConfig.type}
          inputId={fileUploadConfig.type + '-uploader'}
          borderVariant="dashed"
          onFileSelect={handleFileSelect}
          onError={(error) => {
            onError?.(error);

            if (error.message.includes('File too large')) {
              setError(
                `File size exceeds maximum limit of ${fileUploadConfig.maxFileSize / 1024 / 1024}MB. Please try again.`,
              );
            }
          }}
          acceptedFileTypes={fileUploadConfig.acceptedFileTypes}
          maxFileSize={fileUploadConfig.maxFileSize}
          multiple={true}
        />

        {fileUploadConfig.mediaType === 'document' && user && (
          <ProfileDocumentLinkInput
            fileUploadConfig={fileUploadConfig}
            onSubmit={handleLinkSubmit}
          />
        )}
      </Box>

      {error && (
        <Typography
          color={({ backrs }) => backrs.colors['Colors/Text/text-error-primary']}
          variant="text-sm-regular"
        >
          {error}
        </Typography>
      )}

      {isUploading && (
        <Box sx={uploadingContainerSx}>
          <UploadedFile
            fileName={inProgressFileUpload?.name || ''}
            fileSize={`${PROFILE_FORM_DOCUMENT_STRINGS.uploading} (${getFileSize(
              inProgressFileUpload || undefined,
            )})`}
            fileType={inProgressFileUpload?.name.split('.').pop() || ''}
          />
        </Box>
      )}

      {modalSource && !fileUploadConfig.overrideModal && user && (
        <ProfileDocumentUploadModal
          key={fileUploadConfig.type}
          open={modalOpen}
          onClose={() => {
            setModalOpen(false);
            setModalSource(null);
            setFileQueue([]);
            setError(null);
          }}
          mediaType="document"
          source={modalSource}
          onSuccess={() => {
            // process next file in queue
            const remainingFiles = fileQueue.slice(1);

            if (remainingFiles.length > 0) {
              // close the modal
              setModalOpen(false);
              setModalSource(null);

              // reopen the modal with the next file
              setTimeout(() => {
                setFileQueue(remainingFiles);
                setModalSource({
                  kind: 'file',
                  file: remainingFiles[0].file,
                  mediaId: remainingFiles[0].mediaId,
                });
                setModalOpen(true);
              }, 100);
            } else {
              // once all files are processed, close the modal
              setModalOpen(false);
              setModalSource(null);
              setFileQueue([]);
              onSuccess?.();
            }
          }}
          onError={onError}
          modalTitle={fileUploadConfig.title}
          modalDescription={fileUploadConfig.description}
          icon="UploadCloud02"
          fileUploadConfig={fileUploadConfig}
          userId={user.id}
        />
      )}
    </Box>
  );
};

const uploadingContainerSx: SxProps<Theme> = {
  mt: 1,
};

const uploadGroupSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-lg'],
  padding: backrs.spacing['spacing-xl'],
  border: `1px solid ${backrs.colors['Gray/200']}`,
  borderRadius: backrs.radius['radius-xl'],
  backgroundColor: backrs.colors['Colors/Base/White'],
});
